import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import {
  Droplets,
  Scissors,
  Bug,
  ChevronRight,
  TreeDeciduous,
  Users,
  BarChart3,
  Mail,
  Loader2,
  Eye,
  EyeOff
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: "client"
  });

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? "/api/auth/login" : "/api/auth/register";
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${BACKEND_URL}${endpoint}`, payload, {
        withCredentials: true
      });

      setUser(response.data);
      toast.success(isLogin ? "Login realizado!" : "Cadastro realizado!");
      navigate("/dashboard");
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === "string" ? detail : "Erro ao processar requisição";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: TreeDeciduous, title: "Cadastro de Árvores", description: "Registre suas árvores com fotos, medidas e localização GPS automática." },
    { icon: Scissors, title: "Controle de Podas", description: "Acompanhe o histórico completo de podas e manutenções." },
    { icon: Droplets, title: "Irrigação Inteligente", description: "Registre e planeje irrigações para cada árvore do seu jardim." },
    { icon: Bug, title: "Controle Biológico", description: "Monitore pragas e tratamentos aplicados em cada espécie." }
  ];

  const stats = [
    { value: "500+", label: "Árvores Monitoradas" },
    { value: "50+", label: "Agrônomos Ativos" },
    { value: "1000+", label: "Manutenções Registradas" }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass px-4 md:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Mestre Paulo" className="w-12 h-12 rounded-full object-cover" />
            <span className="text-xl font-bold font-['Outfit'] text-primary">Mestre Paulo</span>
          </div>
          <Button data-testid="login-button" onClick={() => setShowAuth(true)} className="btn-primary">
            Entrar
          </Button>
        </div>
      </nav>

      {/* Auth Modal */}
      {showAuth && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-md rounded-2xl card-shadow animate-fade-in">
            <CardHeader className="text-center">
              <img src="/logo.png" alt="Mestre Paulo" className="w-16 h-16 mx-auto rounded-full mb-4" />
              <CardTitle className="text-2xl font-['Outfit'] text-primary">
                {isLogin ? "Entrar" : "Criar Conta"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome completo</Label>
                    <Input
                      id="name"
                      data-testid="register-name"
                      type="text"
                      placeholder="Seu nome"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required={!isLogin}
                    />
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    data-testid="auth-email"
                    type="email"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">Senha</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      data-testid="auth-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Sua senha"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {!isLogin && (
                  <div className="space-y-2">
                    <Label>Tipo de conta</Label>
                    <div className="flex gap-3">
                      <Button
                        type="button"
                        variant={formData.role === "client" ? "default" : "outline"}
                        className={formData.role === "client" ? "btn-primary flex-1" : "flex-1"}
                        onClick={() => setFormData({ ...formData, role: "client" })}
                      >
                        Cliente
                      </Button>
                      <Button
                        type="button"
                        variant={formData.role === "agronomist" ? "default" : "outline"}
                        className={formData.role === "agronomist" ? "btn-primary flex-1" : "flex-1"}
                        onClick={() => setFormData({ ...formData, role: "agronomist" })}
                      >
                        Agrônomo
                      </Button>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="auth-submit"
                  className="w-full btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  {isLogin ? "Entrar" : "Criar Conta"}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  type="button"
                  className="text-sm text-primary hover:underline"
                  onClick={() => setIsLogin(!isLogin)}
                >
                  {isLogin ? "Não tem conta? Cadastre-se" : "Já tem conta? Entre"}
                </button>
              </div>

              <Button
                variant="ghost"
                className="w-full mt-4"
                onClick={() => setShowAuth(false)}
              >
                Cancelar
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 bg-gradient-to-br from-primary/10 via-background to-accent">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 bg-primary rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/50 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 text-center">
          <div className="animate-fade-in">
            <img src="/logo.png" alt="Mestre Paulo" className="w-32 h-32 mx-auto mb-8 rounded-full shadow-2xl" />
            <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm mb-6 font-medium">
              Tecnologia para cuidar da natureza
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-primary mb-6 font-['Outfit']">
              Mestre Paulo
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              A plataforma completa para agrônomos e entusiastas de jardinagem acompanharem o crescimento e saúde de suas árvores.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                data-testid="hero-cta-button"
                onClick={() => setShowAuth(true)}
                size="lg"
                className="btn-primary text-base px-8 py-6"
              >
                Começar Agora
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-primary/50 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-primary/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="text-4xl md:text-5xl font-bold text-white mb-2 font-['Outfit']">{stat.value}</div>
                <div className="text-white/80">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 font-['Outfit'] text-primary">Tudo que você precisa</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">Ferramentas completas para o cuidado profissional de árvores e jardins</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="bg-white border-primary/10 rounded-2xl card-shadow animate-fade-in hover:border-primary/30 transition-colors" style={{ animationDelay: `${index * 0.1}s` }}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2 font-['Outfit'] text-primary">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Agronomist Section */}
      <section className="py-20 md:py-32 bg-accent/30">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-sm mb-4 font-medium">Para Profissionais</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 font-['Outfit'] text-primary">Dashboard completo para agrônomos</h2>
              <p className="text-muted-foreground text-lg mb-8">
                Visualize todas as árvores e clientes em um só lugar. Acompanhe métricas, histórico de manutenções e gere relatórios técnicos com inteligência artificial.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium">Gestão de Clientes</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium">Relatórios com IA</span>
                </div>
              </div>
            </div>
            <div className="relative animate-fade-in stagger-2 flex justify-center">
              <img src="/logo.png" alt="Mestre Paulo" className="w-64 h-64 rounded-full shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 font-['Outfit'] text-primary">Pronto para transformar seu jardim?</h2>
          <p className="text-muted-foreground text-lg mb-8">Junte-se a centenas de agrônomos e jardineiros que já utilizam o Mestre Paulo</p>
          <Button data-testid="cta-button" onClick={() => setShowAuth(true)} size="lg" className="btn-primary text-base px-8 py-6">
            Criar conta gratuita
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Mestre Paulo" className="w-10 h-10 rounded-full" />
              <span className="font-semibold font-['Outfit'] text-lg">Mestre Paulo</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <Mail className="w-4 h-4" />
              <a href="mailto:contato.mestrepaulo@gmail.com" className="hover:text-white transition-colors">contato.mestrepaulo@gmail.com</a>
            </div>
            <p className="text-white/70 text-sm">© 2025 Mestre Paulo. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
